import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';

import { useCurrentUserContext } from 'utils/User';

const assignedAssetGroupingIdsQuery = loader(
  './assignedAssetGroupingIds.graphql',
);

const STORAGE_KEY = 'dashboard:showOnlyAssignedToMe';

const AssignedToMeContext = createContext({
  showOnlyAssignedToMe: false,
  assignedAssetGroupingIds: [],
  customerIds: undefined,
  loading: false,
  toggleAssignedToMe: () => {},
  filterSuffix: '',
});

export function AssignedToMeProvider({ children }) {
  const [showOnlyAssignedToMe, setShowOnlyAssignedToMe] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const { id: userId } = useCurrentUserContext();

  const { data, loading } = useQuery(assignedAssetGroupingIdsQuery, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  });

  const assignedAssetGroupingIds = useMemo(() => {
    if (!data?.sentinelTeam?.assignees?.collection) return [];
    return data.sentinelTeam.assignees.collection
      .map(({ assignable }) => assignable?.id)
      .filter(Boolean);
  }, [data]);

  // Derived value: the customerIds to pass to queries.
  // null when toggle is off; the ID array when filtering (may be empty).
  const customerIds = useMemo(
    () => (showOnlyAssignedToMe ? assignedAssetGroupingIds : null),
    [showOnlyAssignedToMe, assignedAssetGroupingIds],
  );

  const toggleAssignedToMe = useCallback(() => {
    setShowOnlyAssignedToMe((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  // Appends assetGroupingIds filter to a URL path when toggle is on.
  const filterSuffix = useMemo(
    () =>
      showOnlyAssignedToMe && assignedAssetGroupingIds.length > 0
        ? `assetGroupingIds=${assignedAssetGroupingIds.join(',')}`
        : '',
    [showOnlyAssignedToMe, assignedAssetGroupingIds],
  );

  const value = useMemo(
    () => ({
      showOnlyAssignedToMe,
      assignedAssetGroupingIds,
      customerIds,
      loading,
      toggleAssignedToMe,
      filterSuffix,
    }),
    [
      showOnlyAssignedToMe,
      assignedAssetGroupingIds,
      customerIds,
      loading,
      toggleAssignedToMe,
      filterSuffix,
    ],
  );

  return (
    <AssignedToMeContext.Provider value={value}>
      {children}
    </AssignedToMeContext.Provider>
  );
}

AssignedToMeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAssignedToMe() {
  const context = useContext(AssignedToMeContext);
  const { filterSuffix } = context;

  const buildFilteredPath = useCallback(
    (path) => {
      if (!filterSuffix) return path;
      const separator = path.includes('?') ? '&' : '?';
      return `${path}${separator}${filterSuffix}`;
    },
    [filterSuffix],
  );

  return { ...context, buildFilteredPath };
}
